import { useEffect, useRef } from 'react';
import { useEditorStore } from '@/stores/editor-store';
import { updateProject } from '@/lib/api';

const AUTOSAVE_DELAY_MS = 5000;

/**
 * Debounced autosave: saves grid data to API every 5s after changes.
 * Only active when projectId is set (not for new unsaved projects).
 */
export function useAutosave() {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<string>('');

  const projectId = useEditorStore((s) => s.projectId);
  const grid = useEditorStore((s) => s.grid);
  const palette = useEditorStore((s) => s.palette);
  const technique = useEditorStore((s) => s.technique);
  const gridWidth = useEditorStore((s) => s.gridWidth);
  const gridHeight = useEditorStore((s) => s.gridHeight);
  const beadSize = useEditorStore((s) => s.beadSize);
  const projectName = useEditorStore((s) => s.projectName);

  useEffect(() => {
    if (!projectId) return;

    const gridData = {
      technique,
      width: gridWidth,
      height: gridHeight,
      beadSize,
      palette: palette.map((p) => ({
        index: p.index,
        beadId: p.beadId,
        label: p.label,
        count: p.count,
      })),
      grid,
    };

    const snapshot = JSON.stringify(gridData);
    if (snapshot === lastSavedRef.current) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(async () => {
      try {
        await updateProject(projectId, { name: projectName, gridData });
        lastSavedRef.current = snapshot;
      } catch {
        console.error('Autosave failed');
      }
    }, AUTOSAVE_DELAY_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [projectId, grid, palette, technique, gridWidth, gridHeight, beadSize, projectName]);
}
